import autobind from 'autobind-decorator';
import { Inject, Injectable, forwardRef } from '@kaokei/use-vue-service';
import { changeToken } from '@core/api/login';
import { getSignInUserInfo } from '@core/api/user';
import type { UserVO } from '@core/types/user';
import { DURATION29, EXCLUDE_PAGES } from '@core/utils/constant';
import { PERM_ROOT_AUTH } from '../utils/permission';
import { AdtagService } from './adtag.service';
import { BindphoneService } from './bindphone.service';
import { LevelService } from './level.service';
import { LoginService } from './login.service';
import { MessageService } from './message.service';
import { ProfileService } from './profile.service';
import { RouterService } from './router.service';
import { StorageService } from './storage.service';
import { WebSocketService } from './websocket.service';
import { ZoneService } from './zone.service';

@Injectable()
export class UserService {
  // UserService代表内存中的用户数据，刷新页面就会丢失
  // 所以还需要把相关用户数据存储到localStorage中，刷新页面需要从localStorage中获取
  // 目前只有token和user，其他数据尽量转移到自己的服务中进行管理
  public token = '';
  public user = {} as UserVO;

  @Inject(forwardRef(() => ProfileService))
  public profileService!: ProfileService;

  @Inject(forwardRef(() => BindphoneService))
  public bindphoneService!: BindphoneService;

  @Inject(forwardRef(() => LoginService))
  public loginService!: LoginService;

  @Inject(forwardRef(() => RouterService))
  public routerService!: RouterService;

  @Inject(forwardRef(() => StorageService))
  public storageService!: StorageService;

  @Inject(forwardRef(() => AdtagService))
  public adtagService!: AdtagService;

  @Inject(forwardRef(() => ZoneService))
  public zoneService!: ZoneService;

  @Inject(forwardRef(() => MessageService))
  public messageService!: MessageService;

  @Inject(forwardRef(() => LevelService))
  public levelService!: LevelService;

  @Inject(forwardRef(() => WebSocketService))
  public webSocketService!: WebSocketService;

  /**
   * 页面加载时从本地存储中初始化用户数据
   * 如果本地存储没有用户数据，路由中间件可能会强制登录
   */
  public initUserWhenPageLoaded(query: any) {
    // 只在页面加载时持久化adtag
    this.adtagService.setAdtag(query.adtag);
    const token = this.storageService.getUserToken();
    const user = this.storageService.getUserInfo();
    if (token && user) {
      // 先临时使用本地数据，防止用户长时间等待空白页面
      this.token = token;
      this.user = user;
      // 再通过api获取最新的用户数据
      return this.fetchUserInfoFromRemote()
        .catch(() => {
          // 获取用户数据失败需要清除内存中用户数据
          // 注意这里必须是先catch再then
          this.reset();
        })
        .then(() => {
          // 静默获取用户数据失败，这里直接刷新页面
          // 因为本地数据已经被清除，不会再次进入这个逻辑
          // 会被路由中间件拦截，进入首页或者停留在当前页面
          if (this.isLogin()) {
            this.webSocketService.connect();
            const needBindphone = this.needBindphone();
            if (needBindphone) {
              // 优先显示绑定手机号的弹窗
              this.bindphoneService.setVisible(true);
            } else {
              // 然后再显示个人资料弹窗
              this.profileService.setVisible(this.needCompleteProfile());
            }
          } else {
            this.routerService.reload();
          }
        });
    }
  }

  /**
   * 尝试续期token-其实是更换一个新的token
   */
  public tryUpdateToken() {
    if (this.token) {
      const arr = this.token.split('.');
      if (arr.length === 3) {
        const mid = arr[1];
        try {
          const midObj = JSON.parse(window.atob(mid));
          if (midObj.exp) {
            // 过期时间小于29天-这里不是30天是为了避免重复创建新的token
            const expFlag =
              new Date().getTime() + DURATION29 >= midObj.exp * 1000;
            if (expFlag) {
              changeToken().then((res) => {
                this.updateToken(res.token);
                // token 续期：通知 Worker 更新 wsUrl，下次断线重连时使用新 token
                this.webSocketService.connect();
              });
            }
          }
        } catch (error) {
          // ignore
        }
      }
    }
  }

  /**
   * 绑定手机号可能导致token更新
   */
  public updateToken(token: string) {
    this.token = token;
    this.storageService.setUserToken(token);
  }

  /**
   * 绑定手机号可能导致用户信息更新
   */
  public updateUser(user: UserVO) {
    this.user = user;
    this.storageService.setUserInfo(user);
    if (user.token) {
      this.updateToken(user.token);
    }
    this.updateAnalysisSdkConfig(user);
  }

  /**
   * 静默更新用户数据（跨 Tab 登录态同步用）。
   * 只更新内存中的 user 与 token，不写入 localStorage。
   * 区别于 updateUser()：updateUser 会 setUserInfo/setUserToken 写回 localStorage，
   * 在 storage 事件处理中写回同一 key 会触发其他 Tab 的 storage 事件，导致跨 Tab 死循环。
   * 由 storage 事件触发，用于被动感知其他 Tab 的登录/切换账号/完善信息操作。
   * token 优先取 user.token，缺失时保留当前值，不依赖 KEY_USER_LOGIN_TOKEN 事件先到达。
   */
  public updateUserSilent(user: UserVO) {
    this.user = user;
    this.token = user.token || this.token;
    this.updateAnalysisSdkConfig(user);
  }

  /**
   * 更新用户的鼠标拖尾图片
   */
  private updateUserCursorTrails(img: string) {
    this.user.cursorTrails = img;
    this.storageService.setUserInfo(this.user);
  }
  /**
   * 更新用户的头像框图片
   */
  private updateUserAvatarFrame(img: string) {
    this.user.avatarFrame = img;
    this.storageService.setUserInfo(this.user);
  }
  /**
   * 更新用户的主页背景图片
   */
  private updateUserHomeBackground(img: string) {
    this.user.homeBackground = img;
    this.storageService.setUserInfo(this.user);
  }
  /**
   * 更新用户的成绩表装饰图片
   */
  private updateUserGradeDecoration(img: string) {
    this.user.gradeDecoration = img;
    this.storageService.setUserInfo(this.user);
  }

  /**
   * 更换装备的统一入口
   * @param img 装备对应的图片，如果为空则表示卸下装备
   * @param type 装备类型
   */
  public updateUserEquip(img: string, type: number) {
    switch (type) {
      case 1:
        this.updateUserCursorTrails(img);
        break;
      case 2:
        this.updateUserAvatarFrame(img);
        break;
      case 3:
        this.updateUserGradeDecoration(img);
        break;
      case 4:
        this.updateUserHomeBackground(img);
        break;
      default:
        break;
    }
  }

  /**
   * 登录成功之后获取到token
   * 然后再通过token获取用户数据
   */
  public initUserByToken(token: string, reload = true) {
    this.updateToken(token);
    return this.fetchUserInfoFromRemote()
      .then(() => {
        // 登录成功-刷新当前页面
        // 因为是登录弹窗，所以刷新当前页面
        // 因为当前页面可能是既有登录状态又有非登录状态
        if (this.isLogin()) {
          if (reload) {
            // 飞书登录不能直接刷新页面，因为是飞书的回调页面，会导致死循环
            // 会在业务代码中重定向到首页
            this.loginService.hideLoginModal();
            this.routerService.reloadAfterLogin();
          }
        } else {
          throw new Error('获取用户信息失败');
        }
      })
      .catch((e) => {
        this.reset();
        throw e;
      });
  }

  /**
   * 获取服务器端最新的用户数据
   * 并存储到本地存储中
   */
  public fetchUserInfoFromRemote() {
    const adtag = this.adtagService.adtag;
    const loginSource = this.loginService.loginSource;
    return getSignInUserInfo(loginSource, adtag).then((res) => {
      this.updateUser(res);
      // 注意这里代表用户已经登录成功
      // 包括页面加载时静默登录+用户主动登录两种场景都会调用这个方法
      // this.levelService.init(); 激励相关暂时屏蔽
      // 获取用户信息成功后删除adtag
      this.adtagService.removeAdtag();
      // 尝试更新token
      this.tryUpdateToken();
      // 用户登录成功后再获取消息列表
      this.messageService.init();
    });
  }

  private updateAnalysisSdkConfig(user: UserVO) {
    const userId = user?.userId;
    if (userId) {
      window.analysisSdk.update({ userId });
      window.analysisSdk.saLogin(userId);
    }
  }

  public hasPermission(
    perm: string | string[],
    isGroup: boolean, // 是否是小组权限码
    isOnly: boolean, // 是否考虑root_auth，isOnly为true代表不考虑root_auth
  ) {
    if (isGroup) {
      if (isOnly) {
        return this.hasGroupAuthOnly(perm);
      } else {
        return this.hasGroupAuth(perm);
      }
    } else {
      if (isOnly) {
        return this.hasAuthOnly(perm);
      } else {
        return this.hasAuth(perm);
      }
    }
  }
  private isRootManager() {
    return this.user.permissions?.includes(PERM_ROOT_AUTH);
  }
  // 只判断公域权限码
  public hasAuthOnly(perm: string | string[]) {
    const permArr = typeof perm === 'string' ? [perm] : perm;
    return permArr.some((p) => this.user.permissions?.includes(p));
  }
  // root_auth + 公域权限码
  public hasAuth(perm: string | string[]) {
    if (this.isRootManager()) {
      return true;
    }
    return this.hasAuthOnly(perm);
  }
  // 公域权限码 + 小组权限码
  public hasGroupAuthOnly(perm: string | string[]) {
    const publicAuth = this.hasAuthOnly(perm);
    if (publicAuth) {
      return true;
    }
    return this.hasGroupAuthPure(perm);
  }
  // root_auth + 公域权限码 + 小组权限码
  public hasGroupAuth(perm: string | string[]) {
    if (this.hasAuth(perm)) {
      return true;
    }
    return this.hasGroupAuthPure(perm);
  }
  // 只判断小组权限码
  private hasGroupAuthPure(perm: string | string[]) {
    const publicAuth = this.hasAuthOnly(perm);
    if (publicAuth) {
      return true;
    }
    const groupId = this.routerService.getRouteQueryGroupId();
    if (!groupId) {
      return false;
    }
    const curGroup = this.user.myGroup?.find((group) => group.gid === groupId);
    if (!curGroup) {
      return false;
    }
    const permArr = Array.isArray(perm) ? perm : [perm];
    return permArr.some((p) => curGroup.permissions?.includes(p) ?? false);
  }

  public hasNoAuth(perm: string | string[]) {
    return !this.hasAuth(perm);
  }

  public hasNoGroupAuth(perm: string | string[]) {
    return !this.hasGroupAuth(perm);
  }

  public isLogin() {
    return !!this.user.userId;
  }

  public hasAdminPermission() {
    if (this.isAdmin()) {
      return true;
    }
    if (this.user.myGroup) {
      return this.user.myGroup.some((group) => !!group.permissions?.length);
    }
    return false;
  }

  // 只用来控制右上角后台管理的入口展示+后台切换域的选择器
  // 基本只要是有角色的用户isAdmin=true
  // 所以以后增加新的角色，只需要后端注意同时设置isAdmin=true就可以了
  public isAdmin() {
    return this.user.isAdmin;
  }

  public needBindphone() {
    const u = this.user;
    return !u.phoneNumber;
  }

  public needCompleteProfile() {
    const route = this.routerService.getRoute();
    //route.name偶现undefined,但route.meta.name稳定有值，规范上优先取route.name
    const routeName = route.name || route.meta.name;
    const isExcludePage = EXCLUDE_PAGES.includes(routeName);
    if (!routeName || isExcludePage) {
      return false;
    }
    const u = this.user;
    return !u.nickname;
  }

  public reset() {
    // 退出登录时主动销毁 WebSocket 连接，防止残留旧 token 的连接被新登录复用
    this.webSocketService.destroy();
    this.token = '';
    (this.user as any) = {};
    this.storageService.removeUserToken();
    this.storageService.removeUserInfo();
  }

  /**
   * 静默重置：清空内存中的 token/user，但不写入 localStorage。
   * 由 storage 事件触发，用于被动感知其他 Tab 的退出/切换账号操作。
   * 区别于 reset()：不调用 storageService.removeUserToken/removeUserInfo，避免触发死循环。
   */
  private resetSilent(): void {
    this.webSocketService.destroy();
    this.token = '';
    (this.user as any) = {};
  }

  /**
   * localStorage 变化监听器。
   * 只处理 KEY_USER_LOGIN_TOKEN 和 KEY_USER_INFO 两个 key。
   * 使用 @autobind 确保 this 始终指向 UserService 实例。
   */
  @autobind
  private onStorageChange(e: StorageEvent): void {
    // 忽略无关 key
    if (
      e.key !== null &&
      e.key !== StorageService.KEY_USER_LOGIN_TOKEN &&
      e.key !== StorageService.KEY_USER_INFO
    ) {
      return;
    }

    // clear() 或 removeItem → 退出
    if (e.key === null || e.newValue === null) {
      this.resetSilent();
      this.routerService.replace('/');
      return;
    }

    // Token 变化 → 更新内存中的 token，不退出
    if (e.key === StorageService.KEY_USER_LOGIN_TOKEN) {
      this.token = e.newValue;
      // 同一账号 token 续期：通知 Worker 更新 wsUrl，下次断线重连时使用新 token
      if (this.isLogin()) {
        this.webSocketService.connect();
      }
      return;
    }

    // 用户信息变化 → 判断 userId 是否变更
    if (e.key === StorageService.KEY_USER_INFO) {
      try {
        const newUser = JSON.parse(e.newValue) as UserVO;
        if (this.isLogin() && this.user.userId !== newUser.userId) {
          // 已登录，userId 变更（跨 Tab 切换账号）：
          // 断旧连接后通过 updateUserSilent 同步内存中的 user 与 token（不写 localStorage），
          // 建新连接时使用已同步的 this.token，不依赖 KEY_USER_LOGIN_TOKEN 事件先到达
          this.webSocketService.destroy(); // 断旧连接（向 Worker 发 disconnect，清 registeredUrl）
          this.updateUserSilent(newUser);
          this.webSocketService.connect(); // 建新连接（使用已同步的 this.token）
          this.routerService.replace('/');
        } else if (!this.isLogin() && newUser.userId) {
          // 未登录 → 已登录（跨 Tab 登录）：即刻建连
          // updateUserSilent 会从 newUser.token 同步 this.token，不依赖 KEY_USER_LOGIN_TOKEN 事件先到达
          this.updateUserSilent(newUser);
          this.webSocketService.connect();
        } else {
          // 同一用户完善信息 → 仅更新内存（不写 localStorage，防止跨 Tab 循环写回）
          this.updateUserSilent(newUser);
        }
      } catch {
        // JSON 解析失败，静默忽略
      }
    }
  }

  /**
   * 在插件初始化时调用，注册 window storage 事件监听。
   * 无论用户是否已登录都注册，确保非登录 Tab 也能感知其他 Tab 的登录态变化。
   */
  public initStorageListener(): void {
    if (typeof window === 'undefined') return; // SSR 安全
    window.addEventListener('storage', this.onStorageChange);
  }

  // 代理一下LoginService
  // 因为大多数业务只需要UserService而不需要LoginService
  // 从而避免业务代码中需要引入LoginService
  public showLoginModal() {
    if (!this.user.userId) {
      return this.loginService.showLoginModal();
    }
  }

  // 获取准考证号
  public getAdmissionNumber() {
    if (this.user.userId) {
      const ids = `${this.user.userId}`;
      const gap = Math.max(8 - ids.length, 0);
      const zero = '0'.repeat(gap);
      return `H${zero}${ids}`;
    }
  }
}
